import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useApi } from "../../hooks/useApi";
import RepresentmentStartDateField from "./RepresentmentStartDateField";
import { useRepresentmentStartDate } from "../../hooks/useRepresentmentStartDate";

const REQUIRED_ROLES: { role: string; group: string; note?: string }[] = [
  { role: 'Management API - Accounts read', group: 'ACCOUNT' },
  { role: 'Management API - Webhooks read and write', group: 'ACCOUNT' },
  { role: 'Checkout webservice role', group: 'UNCATEGORIZED' },
  { role: 'Merchant Recurring role', group: 'UNCATEGORIZED' },
  { role: 'API dispute management', group: 'UNCATEGORIZED', note: 'to defend chargebacks from the portal; ask Adyen Support if it is not listed' },
];

const INSTRUCTIONS_TEXT = [
  'How to create the Adyen API credentials',
  '',
  '1. Customer Area → Developers → API credentials → Create new credential',
  '2. Credential type: Web service user — set Description to "Chargemont payments"',
  '3. Under Roles, enable these (expand the group, then tick the role):',
  ...REQUIRED_ROLES.map(({ role, group, note }) => `     - ${group} → ${role}${note ? ` (${note})` : ''}`),
  '4. Under Accounts, select "Full company access"',
  '5. Server settings → Authentication → API key → Generate New API Key → copy it (under Server settings, NOT Client settings)',
  '6. Save changes',
  '',
  '7. Paste the API key into Chargemont.',
].join('\n');

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.85em',
        backgroundColor: '#ececf0',
        px: 0.5,
        py: 0.1,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

interface AdyenDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: { adyenAccountId: number; name: string; mappingRequired: boolean }) => void;
  apiUrl: string;
  showRepresentmentStartDate?: boolean;
  environments: ('live' | 'test')[];
}

export default function AdyenDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
  showRepresentmentStartDate,
  environments,
}: AdyenDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState({ name: '', apiKey: '', environment: environments[0] });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const representmentStartDate = useRepresentmentStartDate();

  const handleCopyInstructions = async () => {
    await navigator.clipboard.writeText(INSTRUCTIONS_TEXT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAndClose = () => {
    setFormData({ name: '', apiKey: '', environment: environments[0] });
    representmentStartDate.reset();
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    try {
      const created = await post<{ adyenAccountId: number; name: string; mappingRequired: boolean }>(apiUrl, {
        name: formData.name,
        apiKey: formData.apiKey,
        ...(environments.length > 1 ? { environment: formData.environment } : {}),
        ...(showRepresentmentStartDate ? representmentStartDate.payload : {}),
      }, { slowApi: true });

      resetAndClose();
      await onSuccess(created);
    } catch (err: any) {
      setFormError(err.message || 'Failed to link Adyen account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Adyen Account</DialogTitle>
      <DialogContent>
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{
            mb: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: '#f5f5f7', '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                How to create the API credentials
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  copied ? (
                    <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyInstructions();
                }}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {copied ? 'Copied!' : 'Copy Instructions'}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Box
              sx={{
                color: 'text.secondary',
                fontSize: 14,
                lineHeight: 1.6,
                '& strong': { color: 'text.primary' },
                '& ul': { mt: 0.5, mb: 1.5, pl: 2.5 },
                '& ul > li': { mb: 0.25 },
              }}
            >
              <Box component="ol" sx={{ m: 0, pl: 2.5, '& > li': { mb: 1 } }}>
                <li>
                  Customer Area → <strong>Developers → API credentials → Create new credential</strong>
                </li>
                <li>
                  <strong>Credential type:</strong> Web service user — set <strong>Description</strong> to{' '}
                  <Mono>Chargemont payments</Mono>
                </li>
                <li>
                  Under <strong>Roles</strong>, enable these (expand the group, then tick the role):
                  <Box component="ul">
                    {REQUIRED_ROLES.map(({ role, group, note }) => (
                      <li key={role}>
                        <strong>{group}</strong> → {role}{note && <> ({note})</>}
                      </li>
                    ))}
                  </Box>
                </li>
                <li>
                  Under <strong>Accounts</strong>, select <strong>Full company access</strong>
                </li>
                <li>
                  <strong>Server settings → Authentication → API key → Generate New API Key</strong> → copy it{' '}
                  <Box component="span" sx={{ fontStyle: 'italic' }}>
                    (under Server settings — not Client settings)
                  </Box>
                </li>
                <li>
                  <strong>Save changes</strong>
                </li>
              </Box>

            </Box>
          </AccordionDetails>
        </Accordion>

        {formError && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Account Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          {environments.length > 1 && (
            <TextField
              label="Environment"
              name="environment"
              select
              value={formData.environment}
              onChange={handleInputChange}
              fullWidth
              disabled={submitting}
              size="small"
            >
              {environments.map(environment => (
                <MenuItem key={environment} value={environment}>
                  {environment === 'live' ? 'Live' : 'Test'}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="API Key (web service user)"
            name="apiKey"
            type="password"
            value={formData.apiKey}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          {showRepresentmentStartDate && (
            <RepresentmentStartDateField
              value={representmentStartDate.value}
              onChange={representmentStartDate.setValue}
              disabled={submitting}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !formData.name.trim() || !formData.apiKey || (showRepresentmentStartDate && representmentStartDate.invalid)}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Verifying...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
